import React from 'react';
import { Check, X } from 'lucide-react';

interface BorderedToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  label?: string;
  error?: string;
  className?: string;
  trueLabel?: string;
  falseLabel?: string;
  disabled?: boolean;
}

const BorderedToggle: React.FC<BorderedToggleProps> = ({
  value,
  onChange,
  label,
  error,
  className,
  trueLabel = 'Yes',
  falseLabel = 'No',
  disabled = false,
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!value);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={`group relative rounded cursor-pointer ${error ? 'border-red-500' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className || ''}`}
        style={{
          paddingLeft: '1px',
          paddingRight: '5px',
          display: 'inline-block',
          maxWidth: '200px',
          maxHeight: 36,
        }}
      >
        {label && (
          <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500 z-10 pointer-events-none">
            {label}
          </label>
        )}
        <div
          className={`
            appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
            border-1 border-transparent 
            group-hover:border-gray-500 
            group-hover:border 
            focus:outline-none
            flex items-center justify-between
            ${value ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300'}
            ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500 border border-red-500' : ''}
            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          `}
          onClick={handleToggle}
          style={{
            maxHeight: 36,
          }}
        >
          <div className="flex items-center gap-2">
            {value ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <X className="h-4 w-4 text-gray-400" />
            )}
            <span className={`text-sm mr-1 font-medium ${value ? 'text-green-501' : 'text-gray-600'}`}>
              {value ? trueLabel : falseLabel}
            </span>
          </div>
          <div
            className={`
              w-10 h-5 rounded-full transition-all duration-300 flex items-center
              ${value ? 'bg-green-500' : 'bg-gray-300'}
            `}
          >
            <div
              className={`
                w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-sm
                ${value ? 'translate-x-5' : 'translate-x-0.5'}
              `}
            />
          </div>
        </div>
      </div>
      {error && (
        <span className="mt-1 text-xs text-red-500">
          {error}
        </span>
      )}
    </div>
  );
};

export default BorderedToggle;

